<!--
  PuppyInputSelect 组件。

  文件定位：
  - 该组件用于属性栏中的“所选轮廓 / 所选对象”输入框；
  - 视觉上模拟固定高度的文本框，但内部承载的是受控选择列表；
  - 组件本身不解释场景对象语义，只负责激活、失焦、列表展示与右键菜单。

  核心交互：
  - 点击框内空白区域或边框时，在“激活 / 失焦”之间切换；
  - 点击列表项时保持激活，并把项级交互交给 SelectionListFrame；
  - 点击组件外部或其它表单控件获得焦点时自动失焦；
  - 激活态完全受外部 v-model 控制，组件只负责发出状态变更事件。
-->
<template>
  <div
    ref="rootRef"
    class="puppy-input-select"
    :class="{
      'is-active': resolvedActive,
      'is-disabled': disabled,
    }"
    tabindex="0"
    role="textbox"
    aria-multiline="true"
    :aria-disabled="disabled ? 'true' : 'false'"
    :style="rootStyle"
    @focusin="handleRootFocusIn"
    @mousedown.capture="handleRootMouseDown"
    @keydown="handleRootKeyDown"
  >
    <SelectionListFrame
      class="puppy-input-select__list-frame"
      :style="listFrameStyle"
      :items="items"
      :active-index="activeIndex"
      :selected-indices="selectedIndices"
      :selection-behavior="selectionBehavior"
      :visible-rows="visibleRows"
      :list-role="listRole"
      :empty-text="emptyText"
      :item-key-field="itemKeyField"
      :item-label-field="itemLabelField"
      :get-item-key="getItemKey"
      :is-item-delete-enabled="isItemDeleteEnabled"
      :context-menu-enabled="contextMenuEnabled"
      :disabled="disabled"
      :delete-label="deleteLabel"
      :delete-all-label="deleteAllLabel"
      :show-delete-action="showDeleteAction"
      :show-delete-all-action="showDeleteAllAction"
      @update:active-index="handleActiveIndexUpdate"
      @update:selected-indices="handleSelectedIndicesUpdate"
      @item-click="handleItemClick"
      @delete-item="handleDeleteItem"
      @delete-all="handleDeleteAll"
    >
      <template v-if="$slots.item" #item="slotProps">
        <slot name="item" v-bind="slotProps" />
      </template>
    </SelectionListFrame>
  </div>
</template>

<script setup lang="ts">
/**
 * PuppyInputSelect 逻辑层。
 *
 * 设计说明：
 * - 激活态采用“外部受控 + 组件发出 update:active”的模型；
 * - 组件内部统一收口激活与失焦，不把焦点判断散落到各个属性栏组件中；
 * - 同类组件之间通过显式激活广播协作，实现“点击或聚焦另一个 PuppyInputSelect 时当前组件失焦”；
 * - 鼠标点击与键盘 focusin 共用同一条激活出口，并通过抑制标记避免重复触发；
 * - 只有点击列表项才视为项级交互，点击框内空白或边框用于切换激活态。
 */
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';
import SelectionListFrame from '../editor/SelectionListFrame.vue';

const PUPPY_INPUT_SELECT_ACTIVATE_EVENT = 'puppy-input-select:activate';

interface PuppyInputSelectProps {
  /** 列表数据。 */
  items: unknown[];
  /** 是否处于激活态。 */
  active?: boolean;
  /** 当前激活项索引。 */
  activeIndex?: number;
  /** 当前选中项索引集合。 */
  selectedIndices?: number[];
  /** 可视行数。 */
  visibleRows?: number;
  /** 是否启用右键菜单。 */
  contextMenuEnabled?: boolean;
  /** 空状态文案。 */
  emptyText?: string;
  /** 项目主键字段。 */
  itemKeyField?: string;
  /** 项目显示字段。 */
  itemLabelField?: string;
  /** 自定义主键解析器。 */
  getItemKey?: (item: unknown, index: number) => string | number;
  /** 判断单个列表项是否允许删除。 */
  isItemDeleteEnabled?: (item: unknown, index: number) => boolean;
  /** 选择行为。 */
  selectionBehavior?: 'single' | 'ctrl-multiple';
  /** 列表角色。 */
  listRole?: string;
  /** 是否禁用。 */
  disabled?: boolean;
  /** 删除菜单文案。 */
  deleteLabel?: string;
  /** 删除全部菜单文案。 */
  deleteAllLabel?: string;
  /** 是否显示删除单项菜单。 */
  showDeleteAction?: boolean;
  /** 是否显示删除全部菜单。 */
  showDeleteAllAction?: boolean;
}

const props = withDefaults(defineProps<PuppyInputSelectProps>(), {
  active: false,
  activeIndex: -1,
  selectedIndices: () => [],
  visibleRows: 2,
  contextMenuEnabled: true,
  emptyText: '尚未选择',
  selectionBehavior: 'single',
  disabled: false,
  deleteLabel: '删除',
  deleteAllLabel: '删除所有',
  showDeleteAction: true,
  showDeleteAllAction: true,
});

const emit = defineEmits<{
  /** 同步激活态。 */
  (event: 'update:active', value: boolean): void;
  /** 同步激活项索引。 */
  (event: 'update:activeIndex', value: number): void;
  /** 同步选中项索引集合。 */
  (event: 'update:selectedIndices', value: number[]): void;
  /** 列表项点击。 */
  (event: 'item-click', payload: {
    item: unknown;
    index: number;
    selectedIndices: number[];
    isModifierPressed: boolean;
  }): void;
  /** 删除单项。 */
  (event: 'delete-item', payload: { item: unknown; index: number }): void;
  /** 删除所有。 */
  (event: 'delete-all'): void;
  /** 输入框被激活。 */
  (event: 'activate'): void;
}>();

const rootRef = ref<HTMLElement | null>(null);
const resolvedActive = computed(() => Boolean(props.active));
const rootStyle = computed(() => ({
  overflow: 'hidden',
}));
const listFrameStyle = computed(() => ({
  overflowX: 'hidden',
  overflowY: 'auto',
}));
const instanceId = `puppy-input-select-${Date.now()}-${Math.random().toString(36).slice(2)}`;

/**
 * 标记下一次 focusin 是否应跳过自动激活。
 *
 * 说明：
 * - 鼠标点击会先触发 mousedown，再触发 focusin；
 * - 若 mousedown 已经完成激活/取消激活，则需要跳过紧随其后的 focusin，避免重复发事件。
 */
let shouldSkipNextFocusActivation = false;

/**
 * 判断事件目标是否为 HTML 元素。
 *
 * @param target 事件目标。
 * @returns 是否为 HTML 元素。
 */
function isHTMLElement(target: EventTarget | null): target is HTMLElement {
  return target instanceof HTMLElement;
}

/**
 * 判断目标是否位于列表项上。
 *
 * @param target 事件目标。
 * @returns 是否命中列表项。
 */
function isSelectionItemTarget(target: EventTarget | null): boolean {
  if (!isHTMLElement(target)) {
    return false;
  }
  return Boolean(target.closest('.selection-item'));
}

/**
 * 同步激活态。
 *
 * @param nextActive 目标激活态。
 */
function emitActiveState(nextActive: boolean): void {
  if (props.disabled || resolvedActive.value === nextActive) {
    return;
  }
  emit('update:active', nextActive);
  if (nextActive) {
    emit('activate');
    window.dispatchEvent(new CustomEvent(PUPPY_INPUT_SELECT_ACTIVATE_EVENT, {
      detail: {
        instanceId,
      },
    }));
  }
}

/**
 * 取消输入框激活态。
 */
function deactivate(): void {
  emitActiveState(false);
}

/**
 * 点击根节点时处理激活态切换。
 *
 * 说明：
 * - 点击列表项：保持激活，并允许 SelectionListFrame 继续处理项级交互；
 * - 点击空白区域 / 边框：
 *   - 当前未激活时：进入激活态；
 *   - 当前已激活时：退出激活态。
 *
 * @param event 鼠标事件。
 */
function handleRootMouseDown(event: MouseEvent): void {
  if (props.disabled) {
    return;
  }

  if (isSelectionItemTarget(event.target)) {
    shouldSkipNextFocusActivation = true;
    emitActiveState(true);
    return;
  }

  if (resolvedActive.value) {
    shouldSkipNextFocusActivation = true;
    deactivate();
    rootRef.value?.blur();
    return;
  }

  shouldSkipNextFocusActivation = true;
  emitActiveState(true);
}

/**
 * 根节点获得焦点时同步激活态。
 *
 * @param event 焦点事件。
 */
function handleRootFocusIn(event: FocusEvent): void {
  if (props.disabled) {
    return;
  }

  if (shouldSkipNextFocusActivation) {
    shouldSkipNextFocusActivation = false;
    return;
  }

  if (!rootRef.value || !isHTMLElement(event.target) || !rootRef.value.contains(event.target)) {
    return;
  }

  emitActiveState(true);
}

/**
 * 判断按键是否为删除当前选择的快捷键。
 *
 * @param event 键盘事件。
 * @returns 是否为 Delete / Backspace。
 */
function isDeleteSelectionKey(event: KeyboardEvent): boolean {
  return event.key === 'Delete' || event.key === 'Backspace';
}

/**
 * 读取当前可用于键盘删除的选中索引。
 *
 * @returns 规范化后的选中索引集合。
 */
function resolveKeyboardDeleteIndices(): number[] {
  const selected = props.selectedIndices
    .filter((index) => Number.isInteger(index) && index >= 0 && index < props.items.length);
  if (selected.length > 0) {
    return Array.from(new Set(selected)).sort((left, right) => left - right);
  }
  if (Number.isInteger(props.activeIndex) && props.activeIndex >= 0 && props.activeIndex < props.items.length) {
    return [props.activeIndex];
  }
  return [];
}

/**
 * 处理根节点键盘快捷删除。
 *
 * @param event 键盘事件。
 */
function handleRootKeyDown(event: KeyboardEvent): void {
  if (props.disabled || !resolvedActive.value || !isDeleteSelectionKey(event)) {
    return;
  }
  const indices = resolveKeyboardDeleteIndices();
  if (indices.length === 0) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();

  if (indices.length > 1 && (props.showDeleteAllAction ?? true)) {
    emit('delete-all');
    return;
  }

  if (!(props.showDeleteAction ?? true)) {
    return;
  }

  const index = indices[0];
  const item = props.items[index];
  if (!item || props.isItemDeleteEnabled?.(item, index) === false) {
    return;
  }
  emit('delete-item', { item, index });
}

/**
 * 让宿主表单可以把焦点显式放到当前输入框。
 *
 * 设计说明：
 * - PuppyInputSelect 的 active 仍由外部受控，focus 只负责浏览器焦点；
 * - 部分边线参数切到“参考等距”时，需要先显示参考框再聚焦它，
 *   因此这里提供最小的 DOM 焦点能力，不把业务选择语义塞进基础组件。
 */
function focus(): void {
  rootRef.value?.focus();
}

/**
 * 让宿主表单可以显式移除当前输入框焦点。
 */
function blur(): void {
  rootRef.value?.blur();
}

/**
 * 处理同类组件的激活广播。
 *
 * @param event 自定义事件。
 */
function handlePeerActivate(event: Event): void {
  const activateEvent = event as CustomEvent<{ instanceId?: string }>;
  if (!resolvedActive.value || activateEvent.detail?.instanceId === instanceId) {
    return;
  }
  deactivate();
}

/**
 * 转发激活项索引更新。
 *
 * @param value 新索引。
 */
function handleActiveIndexUpdate(value: number): void {
  emit('update:activeIndex', value);
}

/**
 * 转发选中项集合更新。
 *
 * @param value 新集合。
 */
function handleSelectedIndicesUpdate(value: number[]): void {
  emit('update:selectedIndices', value);
}

/**
 * 转发列表项点击。
 *
 * @param payload 点击载荷。
 */
function handleItemClick(payload: {
  item: unknown;
  index: number;
  selectedIndices: number[];
  isModifierPressed: boolean;
}): void {
  emit('item-click', payload);
}

/**
 * 转发删除单项。
 *
 * @param payload 删除载荷。
 */
function handleDeleteItem(payload: { item: unknown; index: number }): void {
  emit('delete-item', payload);
}

/**
 * 转发删除所有。
 */
function handleDeleteAll(): void {
  emit('delete-all');
}

/**
 * 绑定文档级失焦监听。
 */
onMounted(() => {
  window.addEventListener(PUPPY_INPUT_SELECT_ACTIVATE_EVENT, handlePeerActivate as EventListener);
});

/**
 * 释放文档级失焦监听。
 */
onBeforeUnmount(() => {
  window.removeEventListener(PUPPY_INPUT_SELECT_ACTIVATE_EVENT, handlePeerActivate as EventListener);
});

defineExpose({
  focus,
  blur,
});
</script>

<style scoped>
.puppy-input-select {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  min-height: 92px;
  max-height: 9vh;
  overflow: hidden;
  border: 1px solid var(--n-divider-color);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.03);
  padding: 4px;
  outline: none;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.puppy-input-select.is-active {
  border-color: rgba(47, 140, 255, 0.95);
  background: rgba(47, 140, 255, 0.12);
  box-shadow: inset 0 0 0 1px rgba(47, 140, 255, 0.18);
}

.puppy-input-select.is-disabled {
  opacity: 0.65;
  pointer-events: none;
}

.puppy-input-select__list-frame {
  width: 100%;
  min-height: 0;
}

.puppy-input-select :deep(.selection-list-frame) {
  border: 0;
  border-radius: 0;
  padding: 0;
  min-height: auto;
  background: transparent;
  overflow-x: hidden;
  overflow-y: auto;
}

.puppy-input-select :deep(.selection-list) {
  gap: 6px;
}

.puppy-input-select :deep(.selection-item) {
  min-height: 26px;
  border-color: rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.02);
}

.puppy-input-select :deep(.selection-item.active) {
  border-color: rgba(47, 140, 255, 0.95);
  background: rgba(47, 140, 255, 0.18);
}

.puppy-input-select :deep(.selection-item.selected:not(.active)) {
  border-color: rgba(47, 140, 255, 0.7);
  background: rgba(47, 140, 255, 0.1);
}
</style>
